<template>
    <CqModal :showModal="showModal" :preventClosing="isSaving" @closeModal="closeModal" width="fit-content" maxWidth="90vw" heading="Sneltoetsen">
        <div id="shortcutContainer">
            <div class="shortcuts" v-if="openedShortcutPreferences">
                <div>
                    <InfoGrid>
                        <InfoHeader>Dienst kopiëren</InfoHeader>
                        <ShortcutModalInput :shortcut="openedShortcutPreferences.copy" :type="ShortcutType.copy" @toggle:waitingForInput="waitingForInputHandler" disabled />
                        <InfoHeader>Dienst plakken</InfoHeader>
                        <ShortcutModalInput :shortcut="openedShortcutPreferences.paste" :type="ShortcutType.paste" @toggle:waitingForInput="waitingForInputHandler" disabled />
                        <InfoHeader>Dienst knippen</InfoHeader>
                        <ShortcutModalInput :shortcut="openedShortcutPreferences.cut" :type="ShortcutType.cut" @toggle:waitingForInput="waitingForInputHandler" disabled />
                        <InfoHeader>Dienst bewerken</InfoHeader>
                        <ShortcutModalInput :shortcut="openedShortcutPreferences.editDeployment" :type="ShortcutType.editDeployment" @toggle:waitingForInput="waitingForInputHandler" @change:shortcut="changeShortcutHandler" />
                        <InfoHeader>Dienst toevoegen</InfoHeader>
                        <ShortcutModalInput :shortcut="openedShortcutPreferences.newDeployment" :type="ShortcutType.newDeployment" @toggle:waitingForInput="waitingForInputHandler" @change:shortcut="changeShortcutHandler" />
                    </InfoGrid>
                </div>
                <div>
                    <InfoGrid>
                        <template v-for="(label, shortcut) in stateShortcuts" :key="shortcut">
                            <InfoHeader>{{ label }}</InfoHeader>
                            <ShortcutModalInput :shortcut="getShortcutByType(shortcut as ShortcutType)" :type="shortcut as ShortcutType" @toggle:waitingForInput="waitingForInputHandler" @change:shortcut="changeShortcutHandler" />
                        </template>
                    </InfoGrid>
                </div>
            </div>
            <CqButtonBar>
                <CqButton primary label="Opslaan" icon="save" @click="save" :disabled="isSaving" />
            </CqButtonBar>
        </div>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqModal} from '@cquential/ui';
    import ShortcutModalInput from '@/components/modals/ShortcutModalInput.vue';
    import {ApiClient, OrganisationDeploymentState, DeploymentState, ApiError} from '@cquential/api';
    import {RosterDay} from '@/types/types';
    import {ShortcutType} from '@/types/enums/ShortcutType';
    import {KeyControlSetting} from '@/components/deploymentAutocomplete/types';
    import {DEFAULT_SHORTCUTS} from '@/util/Defaults';
    import Notify from '@/components/modals/Notify.vue';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import {toRaw} from 'vue';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            ShortcutModalInput,
            InfoGrid,
            InfoHeader,
        },
        emits: ['change:shortcuts'],
    })
    export default class ShortcutModal extends Vue {
        private static instance: ShortcutModal | null;

        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public rosterDays!: RosterDay[];
        @Prop() public shortcutPreferences?: Record<ShortcutType, KeyControlSetting>;

        public openedShortcutPreferences: Record<ShortcutType, KeyControlSetting> | null = null;
        public showModal: boolean = false;
        public isSaving: boolean = false;
        public waitingForInput: boolean = false;
        public ShortcutType: typeof ShortcutType = ShortcutType;

        public mounted(): void {
            ShortcutModal.instance = this;
        }

        public beforeUnmount(): void {
            ShortcutModal.instance = null;
        }

        public get stateShortcuts(): Partial<Record<ShortcutType, string>> {
            const result: Partial<Record<ShortcutType, string>> = {};
            for (const action in this.openedShortcutPreferences ?? {}) {
                for (const stateIndex in this.organisationDeploymentStates) {
                    const state: OrganisationDeploymentState = this.organisationDeploymentStates[stateIndex];
                    if (state && this.doesShortcutExistForState(state.id, action)) {
                        if (state.beginDate <= this.rosterDays[this.rosterDays.length - 1].date && state.endDate >= this.rosterDays[0].date) {
                            result[action as ShortcutType] = state.caption;
                        }
                        break; // break inner loop
                    }
                }
            }

            return result;
        }

        private doesShortcutExistForState(stateId: DeploymentState, shortcutName: string): boolean {
            return (stateId === DeploymentState.Normal && shortcutName === ShortcutType.normal)
                || (stateId === DeploymentState.Free && shortcutName === ShortcutType.free)
                || (stateId === DeploymentState.Sick && shortcutName === ShortcutType.sick)
                || (stateId === DeploymentState.Extra && shortcutName === ShortcutType.extra)
                || (stateId === DeploymentState.Training && shortcutName === ShortcutType.training)
                || (stateId === DeploymentState.CompensationLeave && shortcutName === ShortcutType.compensationLeave)
                || (stateId === DeploymentState.SpecialLeave && shortcutName === ShortcutType.specialLeave);
        }

        public getShortcutByType(type: ShortcutType): KeyControlSetting | undefined {
            return this.openedShortcutPreferences?.[type];
        }

        private waitingForInputHandler(newValue: boolean): void {
            this.waitingForInput = newValue;
        }

        private changeShortcutHandler(shortcut: KeyControlSetting, type: ShortcutType): void {
            this.openedShortcutPreferences![type] = shortcut;
        }

        private save(): void {
            if (this.isSaving) {
                return;
            }

            let shortcut: ShortcutType;
            const promises: Promise<void>[] = [];
            this.isSaving = true;
            for (shortcut in this.openedShortcutPreferences!) {
                const defaultShortcut: KeyControlSetting = DEFAULT_SHORTCUTS[shortcut];
                const shortcutToCheck: KeyControlSetting = this.openedShortcutPreferences![shortcut];

                let promise: Promise<void>;

                if (
                    defaultShortcut.key !== shortcutToCheck.key
                    || defaultShortcut.ctrlKey !== shortcutToCheck.ctrlKey
                    || defaultShortcut.shiftKey !== shortcutToCheck.shiftKey
                    || defaultShortcut.altKey !== shortcutToCheck.altKey
                    || defaultShortcut.metaKey !== shortcutToCheck.metaKey
                ) {
                    promise = ApiClient.instance.savePreference({
                        name: 'shortcut' + shortcut,
                        value: JSON.stringify(shortcutToCheck),
                    });
                } else {
                    promise = ApiClient.instance.deletePreference('shortcut' + shortcut);
                }

                promise.catch((): void => {
                    console.error(`Unable to save preference ${'shortcut' + shortcut}.`);
                });

                promises.push(promise);
            }

            if (promises.length > 0) {
                Promise.all(promises).then((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Sneltoetsen zijn opgeslagen.', type: 'success'});

                    this.$emit('change:shortcuts', this.openedShortcutPreferences!);
                    this.isSaving = false;
                    this.closeModal();
                }).catch((error: ApiError): void => {
                    this.isSaving = false;
                    console.error(error);
                });
            } else {
                this.isSaving = false;
            }
        }

        public static show(): void {
            if (!this.instance!.showModal) {
                this.instance!.openedShortcutPreferences = structuredClone(toRaw(this.instance!.shortcutPreferences)) ?? null;
                this.instance!.showModal = true;
            }
        }

        public static closeModal(): void {
            this.instance!.closeModal();
        }

        private closeModal(): void {
            if (this.waitingForInput || this.isSaving) {
                return;
            }

            this.showModal = false;
        }

        public static isWaitingForInput(): boolean {
            return this.instance!.waitingForInput;
        }
    }
</script>

<style lang="scss" scoped>
    #shortcutContainer {
        width: clamp(40rem, 40vw, 50rem);

        .shortcuts {
            display: grid;
            gap: 1rem;
            grid-template-columns: 1fr 1fr;
        }
    }
</style>
